import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CircleQuestionMark, Plus, Trash2 } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useDateFormatter } from "@/hooks/useUserPreferences";
import { authClient } from "@/lib/auth";
import { AlertDialogType } from "@/types/alert-dialog";
import { type AlertDialogImperativeHandle, DefaultAlertDialog } from "../DefaultAlertDialog";
import LoadingSpinner from "../loading-spinner";
import CreateApiKeyDialog from "./CreateApiKeyDialog";
import RevealedApiKeyDialog from "./RevealedApiKeyDialog";

type ApiKey = {
    id: string;
    name: string | null;
    start: string | null;
    prefix: string | null;
    createdAt: Date | string;
    expiresAt: Date | string | null;
    lastRequest: Date | string | null;
    metadata: { organizationId?: string } | null;
};

export default function ApiKeysSettings() {
    const queryClient = useQueryClient();
    const dateFormatter = useDateFormatter();
    const formatDate = (value: Date | string | null) => {
        if (!value) return "—";
        return dateFormatter.formatDate(typeof value === "string" ? new Date(value) : value);
    };
    const deleteAlertDialogRef = useRef<AlertDialogImperativeHandle | null>(null);
    const pendingDeleteId = useRef<string | null>(null);

    const [createOpen, setCreateOpen] = useState(false);
    const [revealedKey, setRevealedKey] = useState<string | null>(null);

    const { data: activeOrganization } = authClient.useActiveOrganization();
    const activeOrgId = activeOrganization?.id;

    const listQuery = useQuery({
        queryKey: ["api-keys", activeOrgId],
        enabled: !!activeOrgId,
        queryFn: async () => {
            const result = await (authClient as any).apiKey.list();
            if (result.error) throw new Error(result.error.message);
            const data = result.data;
            const apiKeys = Array.isArray(data) ? data : (data?.apiKeys ?? []);
            return (apiKeys as ApiKey[]).filter((key) => key.metadata?.organizationId === activeOrgId);
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async (keyId: string) => {
            const result = await authClient.apiKey.delete({ keyId });
            if (result.error) throw new Error(result.error.message);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["api-keys", activeOrgId] });
            toast.success("API key deleted");
        },
        onError: (error) => {
            toast.error("Failed to delete API key", { description: error.message });
        },
    });

    const handleDelete = (keyId: string) => async () => {
        pendingDeleteId.current = keyId;
        const confirmed = await deleteAlertDialogRef.current!.show();
        if (confirmed && pendingDeleteId.current) {
            deleteMutation.mutate(pendingDeleteId.current);
        }
        pendingDeleteId.current = null;
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex justify-end items-center">
                <Button variant="outline" onClick={() => setCreateOpen(true)}>
                    <Plus />
                    Add API key
                </Button>
            </div>

            {listQuery.isLoading ? (
                <LoadingSpinner />
            ) : (
                <div className="rounded-lg border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Created</TableHead>
                                <TableHead>Expires</TableHead>
                                <TableHead>Last used</TableHead>
                                <TableHead></TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {(listQuery.data ?? []).length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                                        No API keys yet
                                    </TableCell>
                                </TableRow>
                            ) : (
                                listQuery.data!.map((key) => (
                                    <TableRow key={key.id} className="h-12">
                                        <TableCell className="font-medium">{key.name || "Unnamed"}</TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {formatDate(key.createdAt)}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {key.expiresAt ? formatDate(key.expiresAt) : "Never"}
                                        </TableCell>
                                        <TableCell className="text-muted-foreground">
                                            {formatDate(key.lastRequest)}
                                        </TableCell>
                                        <TableCell className="w-12">
                                            <Button variant="ghost" size="icon" onClick={handleDelete(key.id)}>
                                                <Trash2 />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </div>
            )}
            <div className="flex flex-col gap-1">
                <p className="text-sm text-orsane-muted flex items-center gap-1">
                    <CircleQuestionMark className="size-4" />
                    Read the{" "}
                    <a
                        href="https://www.orsane.app/docs/reference/api/overview"
                        target="_blank"
                        rel="noopener"
                        className="underline underline-offset-4 hover:text-primary"
                    >
                        API documentation
                    </a>{" "}
                    to get started.
                </p>
            </div>

            <CreateApiKeyDialog
                open={createOpen}
                onOpenChange={setCreateOpen}
                onCreated={({ key }) => setRevealedKey(key)}
            />

            <RevealedApiKeyDialog apiKey={revealedKey} onClose={() => setRevealedKey(null)} />

            <DefaultAlertDialog
                ref={deleteAlertDialogRef}
                type={AlertDialogType.Delete}
                title="Delete API key"
                description="Any service using this key will stop working immediately. This cannot be undone."
            />
        </div>
    );
}
